import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles, Text, Button, Badge, Spinner } from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  QrCodeRegular,
  LocationRegular,
  DeleteRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, ProtectedButton } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  fullDate,
  API_URL,
} from '../../../configurations/index.config';
import type { Checkpoint } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  actions: {
    display: 'flex',
    gap: '12px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#323130',
  },
  qrSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '24px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
  },
  qrImage: {
    maxWidth: '300px',
    border: '2px solid #edebe9',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#ffffff',
  },
  statsCard: {
    padding: '16px',
    backgroundColor: '#eff6fc',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#0078d4',
  },
  statLabel: {
    fontSize: '12px',
    color: '#605e5c',
  },
});

const CheckpointDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [checkpoint, setCheckpoint] = useState<Checkpoint | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [qrName, setQrName] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (id) {
      fetchCheckpoint();
      fetchQRImage();
    }
  }, [id]);

  const fetchCheckpoint = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get(`/get-checkpoint/${id}`);
      if (response.data.status === 'success') {
        setCheckpoint(response.data.data);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
      navigate('/account/checkpoints');
    }
    setLoading(false);
  };

  const fetchQRImage = async () => {
    try {
      const response = await ApiClient.get(`/get-qr-image/${id}`);
      if (response.data.status === 'success' && response.data.data.image_exists) {
        setQrImageUrl(`${API_URL}${response.data.data.qr_image_url}`);
        setQrName(response.data.data.qr_name || null);
      }
    } catch (error: unknown) {
      console.log('QR image not generated yet');
    }
  };

  const handleGenerateQR = async () => {
    if (!checkpoint) {
      return;
    }

    try {
      const response = await ApiClient.post('/generate-qr-batch', {
        checkpoint_ids: [checkpoint.id],
      });

      if (response.data.status === 'success') {
        newToast('success', 'QR code generated successfully');
        fetchQRImage();
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const renderQRWithLabel = useCallback(() => {
    if (!qrImageUrl || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const labelHeight = 50;
      canvas.width = img.width;
      canvas.height = img.height + labelHeight;

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw QR image
      ctx.drawImage(img, 0, 0);

      // Draw label at bottom
      const displayName = qrName || `QR-${checkpoint?.checkpoint_name}`;
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 18px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(displayName, canvas.width / 2, img.height + 32);
    };
    img.src = qrImageUrl;
  }, [qrImageUrl, qrName, checkpoint?.checkpoint_name]);

  useEffect(() => {
    renderQRWithLabel();
  }, [renderQRWithLabel]);

  const handleDownloadQR = () => {
    if (!canvasRef.current) {
      return;
    }

    const downloadName = qrName || `QR-${checkpoint?.checkpoint_name}`;
    const link = document.createElement('a');
    link.download = `${downloadName}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const handleDelete = async () => {
    if (!checkpoint) {
      return;
    }

    if (!window.confirm(`Delete checkpoint "${checkpoint.checkpoint_name}"?`)) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-checkpoint', {
        id: checkpoint.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Checkpoint deleted successfully');
        navigate('/account/checkpoints');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading checkpoint details..." />
      </div>
    );
  }

  if (!checkpoint) {
    return null;
  }

  return (
    <PermissionGate privilege="checkpoints.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <Button
              appearance="subtle"
              icon={<ArrowLeftRegular />}
              onClick={() => navigate('/account/checkpoints')}
            />
            <div>
              <Text className={styles.pageTitle}>{checkpoint.checkpoint_name}</Text>
              <Badge
                appearance="tint"
                color={checkpoint.status === 'active' ? 'success' : 'warning'}
                style={{ marginLeft: '12px' }}
              >
                {checkpoint.status}
              </Badge>
            </div>
          </div>

          <div className={styles.actions}>
            <ProtectedButton
              privilege="checkpoints.delete"
              appearance="secondary"
              icon={<DeleteRegular />}
              onClick={handleDelete}
            >
              Delete
            </ProtectedButton>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          <ContentCard>
            <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '24px' }}>
              Checkpoint Information
            </Text>

            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Client</Text>
                <Text className={styles.detailValue}>{checkpoint.client_name || '—'}</Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Site</Text>
                <Text className={styles.detailValue}>{checkpoint.site_name || '—'}</Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Validation Radius</Text>
                <Text className={styles.detailValue}>{checkpoint.validation_radius_m} meters</Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Description</Text>
                <Text className={styles.detailValue}>{checkpoint.description || '—'}</Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Location Description</Text>
                <Text className={styles.detailValue}>{checkpoint.location_description || '—'}</Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>GPS Coordinates</Text>
                <Text className={styles.detailValue}>
                  {checkpoint.expected_gps_lat && checkpoint.expected_gps_lng
                    ? `${checkpoint.expected_gps_lat}, ${checkpoint.expected_gps_lng}`
                    : 'Not configured'}
                </Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>QR Token</Text>
                <Text
                  className={styles.detailValue}
                  style={{ fontSize: '11px', fontFamily: 'monospace' }}
                >
                  {checkpoint.qr_token}
                </Text>
              </div>

              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Created At</Text>
                <Text className={styles.detailValue}>{fullDate(checkpoint.created_at)}</Text>
              </div>

              {checkpoint.updated_at && (
                <div className={styles.detailItem}>
                  <Text className={styles.detailLabel}>Last Updated</Text>
                  <Text className={styles.detailValue}>{fullDate(checkpoint.updated_at)}</Text>
                </div>
              )}
            </div>
          </ContentCard>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <ContentCard>
              <div className={styles.statsCard}>
                <Text className={styles.statValue}>{checkpoint.total_scans || 0}</Text>
                <Text className={styles.statLabel}>Total Scans</Text>
              </div>
            </ContentCard>

            <ContentCard>
              <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
                QR Code
              </Text>

              <div className={styles.qrSection}>
                {qrImageUrl ? (
                  <>
                    <canvas
                      ref={canvasRef}
                      className={styles.qrImage}
                      style={{ maxWidth: '300px' }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <ProtectedButton
                        privilege="qr.generate"
                        appearance="primary"
                        onClick={handleDownloadQR}
                      >
                        Download QR
                      </ProtectedButton>
                    </div>
                  </>
                ) : (
                  <>
                    <LocationRegular style={{ fontSize: '48px', color: '#c8c6c4' }} />
                    <Text style={{ color: '#605e5c', textAlign: 'center' }}>
                      QR code not generated yet
                    </Text>
                    <ProtectedButton
                      privilege="qr.generate"
                      appearance="primary"
                      icon={<QrCodeRegular />}
                      onClick={handleGenerateQR}
                    >
                      Generate QR Code
                    </ProtectedButton>
                  </>
                )}
              </div>
            </ContentCard>
          </div>
        </div>
      </div>
    </PermissionGate>
  );
};

export default CheckpointDetailsFluentUI;
